import { useParams, Link, Navigate } from 'react-router-dom';
import { CALCULATORS, TRADES } from '@/lib/calculators';
import { guideForCalc } from '@/lib/guides';
import CalculatorCard from '@/components/CalculatorCard';
import BuyMaterials from '@/components/BuyMaterials';
import { buyKeywordsFor } from '@/lib/affiliateShop';
import Seo from '@/components/Seo';
import { ArrowLeft, BookOpen, ArrowRight } from 'lucide-react';

const titleFor = (name) => (/calculat/i.test(name) ? name : `${name} Calculator`);

export default function CalculatorPage() {
  const { slug } = useParams();
  const calc = CALCULATORS.find((c) => c.slug === slug);
  if (!calc) return <Navigate to="/calculators" replace />;

  const trade = TRADES.find((t) => t.slug === calc.trade);
  const heading = titleFor(calc.name);
  const desc = `${calc.blurb} Free, instant UK trade calculator from TradesMate — no sign-up needed.`.slice(0, 320);
  const related = CALCULATORS.filter((c) => c.trade === calc.trade && c.slug !== calc.slug).slice(0, 6);
  const guide = guideForCalc(calc.slug);
  const buyKeywords = buyKeywordsFor(calc);

  const faqs = [
    { q: `How do I use the ${heading.toLowerCase()}?`, a: `Enter your measurements and options into the ${calc.name} above and the result updates instantly. ${calc.blurb}` },
    { q: `Is the ${heading.toLowerCase()} free to use?`, a: `Yes — every TradesMate calculator is free to use with no sign-up. Save quotes and use the full estimator with a free trial.` },
    { q: `Are the results accurate enough to quote from?`, a: `The figures are reliable estimates based on standard UK trade practice. Always allow for wastage and verify against current material prices before quoting a customer.` },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Calculators', item: 'https://tradesmateuk.co.uk/calculators' },
        { '@type': 'ListItem', position: 2, name: heading, item: `https://tradesmateuk.co.uk/calculators/${calc.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title={heading} description={desc} path={`/calculators/${calc.slug}`} jsonLd={jsonLd} kicker="// Free UK Trade Calculator" />

      <nav className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2 flex-wrap font-semibold">
        <Link to="/calculators" className="hover:text-brand">Calculators</Link>
        <span>/</span>
        {trade && <Link to={`/trades/${trade.slug}`} className="hover:text-brand">{trade.name}</Link>}
      </nav>

      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight">{heading}</h1>
        <p className="text-slate-600 mt-3">{calc.blurb}</p>
      </header>

      <CalculatorCard calc={calc} />

      {guide && (
        <Link
          to={`/guides/${guide.slug}`}
          data-testid="calc-guide-link"
          className="mt-6 tm-card p-4 flex items-center gap-3 hover:shadow-lg transition-shadow"
        >
          <BookOpen className="text-brand shrink-0" size={22} />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Read the guide</p>
            <p className="font-display font-bold uppercase tracking-tight leading-tight">{guide.h1}</p>
          </div>
          <ArrowRight className="text-brand shrink-0" size={18} />
        </Link>
      )}

      <BuyMaterials keywords={buyKeywords} />

      <section className="mt-10">
        <h2 className="font-display font-bold text-xl uppercase mb-4">Frequently asked questions</h2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="tm-card p-4">
              <h3 className="font-bold text-base">{f.q}</h3>
              <p className="text-slate-600 text-sm mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display font-bold text-xl uppercase mb-4">Related calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {related.map((c) => (
              <Link key={c.slug} to={`/calculators/${c.slug}`} className="tm-card p-3 hover:shadow-lg transition-shadow flex items-center justify-between">
                <span className="font-medium text-sm">{titleFor(c.name)}</span>
                <span className="text-brand">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 tm-card p-5 bg-slate-900 text-white">
        <h2 className="font-display font-bold text-lg uppercase">Turn your numbers into a winning quote</h2>
        <p className="text-slate-300 text-sm mt-1">Use the full TradesMate estimator to build branded, professional quotes in minutes.</p>
        <Link to="/estimator" className="tm-btn mt-3 inline-flex">Open the estimator →</Link>
      </div>

      <Link to="/calculators" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand mt-8 font-semibold"><ArrowLeft size={14}/> All calculators</Link>
    </div>
  );
}
